import type {
  EventCommitment,
  EventColor,
  EventRequirement,
  OperationEvent,
} from "../../domain/operations";
import type { InvoiceImportSource, ParsedInvoiceDraft, ParsedInvoiceLine } from "./types";

const EVENT_COLORS: EventColor[] = ["blue", "plum", "amber"];

function slugify(value: string): string {
  const slug = value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 40);
  return slug || "imported-event";
}

function pickColor(seed: string): EventColor {
  let hash = 0;
  for (let index = 0; index < seed.length; index += 1) {
    hash = (hash + seed.charCodeAt(index)) % EVENT_COLORS.length;
  }
  return EVENT_COLORS[hash] ?? "blue";
}

function uniqueEventId(baseName: string, existingIds: Set<string>): string {
  const baseId = slugify(baseName);
  if (!existingIds.has(baseId)) return baseId;
  let suffix = 2;
  while (existingIds.has(`${baseId}-${suffix}`)) suffix += 1;
  return `${baseId}-${suffix}`;
}

function resolveEventName(draft: ParsedInvoiceDraft): string {
  if (draft.projectName?.trim()) return draft.projectName.trim();
  const client = draft.clientName?.trim();
  const type = draft.eventType?.trim();
  if (client && type) return `${client} — ${type}`;
  if (type) return type;
  if (client) return `${client} Event`;
  return "Imported Event";
}

function lineDetail(line: ParsedInvoiceLine): string {
  const qty = line.interpretedQuantity ?? line.rawQuantity;
  const unit = line.interpretedUnit?.trim() || line.rawUnitLabel?.trim();
  const reviewMeta = [
    qty ? `Qty ${qty}` : null,
    unit ? `Unit ${unit}` : null,
  ].filter(Boolean).join(" · ");
  const parts = [reviewMeta, line.description?.trim(), line.rawLine].filter(Boolean);
  return parts.join(" · ") || line.name;
}

function mapReviewedLines(id: string, draft: ParsedInvoiceDraft): {
  commitments: EventCommitment[];
  requirements: EventRequirement[];
} {
  const commitments: EventCommitment[] = [];
  const requirements: EventRequirement[] = [];

  for (const line of draft.lines) {
    switch (line.classification) {
      case "commercial_only":
        break;
      case "menu_item":
      case "service_commitment":
      case "station_setup":
        commitments.push({
          id: `${id}-commit-${line.id}`,
          title: line.name,
          detail: lineDetail(line),
        });
        break;
      case "equipment_resource":
        requirements.push({
          id: `${id}-req-${line.id}`,
          title: line.name,
          detail: lineDetail(line),
          critical: false,
          resolved: false,
        });
        break;
      case "rental_third_party":
        commitments.push({
          id: `${id}-rental-commit-${line.id}`,
          title: line.name,
          detail: `[Rental / third party] ${lineDetail(line)}`,
        });
        requirements.push({
          id: `${id}-rental-req-${line.id}`,
          title: line.name,
          detail: lineDetail(line),
          critical: false,
          resolved: false,
        });
        break;
    }
  }

  return { commitments, requirements };
}

export function validateReviewForEventCreation(draft: ParsedInvoiceDraft): string[] {
  const errors: string[] = [];
  if (!draft.clientName?.trim()) errors.push("Client name is required.");
  if (!draft.invoiceNumber?.trim()) errors.push("Invoice number is required.");
  if (!draft.projectName?.trim() && !draft.eventType?.trim()) {
    errors.push("Project name or event type is required.");
  }
  if (draft.lines.some((line) => line.needsReview)) {
    const count = draft.lines.filter((line) => line.needsReview).length;
    errors.push(
      count === 1
        ? "Resolve 1 review item before creating this Event."
        : `Resolve ${count} review items before creating this Event.`,
    );
  }
  return errors;
}

export function draftToEvent(
  draft: ParsedInvoiceDraft,
  source: InvoiceImportSource,
  importCandidateId: string,
  existingEventIds: Set<string>,
): OperationEvent {
  const name = resolveEventName(draft);
  const id = uniqueEventId(name, existingEventIds);
  const { commitments, requirements } = mapReviewedLines(id, draft);

  const contacts = [];
  if (draft.clientName?.trim()) {
    contacts.push({
      id: `${id}-client`,
      name: draft.clientName.trim(),
      role: "Client",
      phone: draft.clientPhone?.trim(),
      external: true,
    });
  }
  if (draft.clientEmail?.trim()) {
    contacts.push({
      id: `${id}-client-email`,
      name: draft.clientEmail.trim(),
      role: "Client email",
      external: true,
    });
  }

  const conditions = draft.parseWarnings.map((warning, index) => ({
    id: `${id}-warn-${index}`,
    title: "Import parse note",
    detail: warning,
    severity: "caution" as const,
  }));

  return {
    id,
    name,
    type: draft.eventType?.trim() || "Event",
    status: "inquiry",
    color: pickColor(id),
    client: draft.clientName?.trim() || "Unknown client",
    dateLabel: draft.eventDate?.trim() || "Date TBD",
    dateRangeLabel: draft.eventDate?.trim(),
    location: draft.venue?.trim(),
    contacts,
    keyTimes: [],
    flow: [],
    commitments,
    requirements,
    conditions,
    sourceInvoice: {
      importCandidateId,
      sourceKind: source.kind,
      filename: source.filename,
      sourceUrl: source.url,
      invoiceNumber: draft.invoiceNumber?.trim(),
      invoiceIssueDate: draft.invoiceIssueDate?.trim(),
      importedAt: new Date().toISOString(),
      reviewedDraft: structuredClone(draft),
    },
  };
}
